import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HTTP_TIMEOUT_MS } from '../../config';
import {
  PaperAirplaneIcon,
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { X } from 'lucide-react';
import { SearchInput, AppsStack, DataTable, DataTableColumn, DataTableAction, Pagination, ActiveFilter, PageHeader, ConfirmationModal, useToastActions, useDataTableUrl, useClientPage } from '@airclou/ui';
import { TABLE_CONFIG } from '@airclou/shared';
import { dashboardApiClient } from '../../services/api';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

// The server derives status from the row itself: accepted_at, then expires_at.
// There is deliberately no 'cancelled' — cancelling deletes the invitation, so
// a cancelled one is absent rather than labelled.
interface Invitation {
  id: string;
  email: string;
  role: 'admin' | 'author' | 'contributor' | 'viewer';
  apps: Array<{ id: string; name: string; invitationId: string }>;
  status: 'pending' | 'expired' | 'accepted';
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
}

interface InvitationsPageProps {
  hideHeader?: boolean;
  /** Bumped by the parent after an invite lands, to re-run the fetch. */
  refreshKey?: number;
}

export function InvitationsPage({ hideHeader = false, refreshKey = 0 }: InvitationsPageProps) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [fetchingInvitations, setFetchingInvitations] = useState(true);
  // Whether the last fetch produced a list. A failed one leaves `invitations`
  // empty, which must not read as "one page" and move the reader off theirs.
  const [invitationsLoaded, setInvitationsLoaded] = useState(false);
  const { success: toastSuccess, error: toastError } = useToastActions();
  // The id of the invitation whose resend or cancel is in flight. Both actions
  // are disabled for that row while it is set, so a slow request cannot be
  // fired twice or raced against its own cancel.
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null);
  // Search and page live in the URL (same pattern as the audit log), so they
  // survive a reload, a shared link and back/forward. `sorting` is wired only
  // because DataTable requires it: no column here sorts. Before making one
  // sortable, sort the whole list ahead of `useClientPage` (as the SSO audit
  // tab does) — the table only ever sees one page, and sorting that page alone
  // strands the row that should be first on whichever page it happens to fall.
  const {
    search: searchQuery,
    page,
    limit: pageSize,
    sorting,
    onSortingChange: handleSortingChange,
    onSearchChange: handleSearchChange,
    onPageChange,
    clampPage,
  } = useDataTableUrl({ defaultLimit: TABLE_CONFIG.DEFAULT_PAGE_SIZE });

  // Fetch invitations on mount, and again whenever the parent reports a new invite.
  useEffect(() => {
    fetchInvitations();
  }, [refreshKey]);

  const fetchInvitations = async () => {
    setFetchingInvitations(true);
    setInvitationsLoaded(false);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('dashboard:authToken');

      const response = await fetch('/api/team/invitations', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setInvitations(result.data);
          setInvitationsLoaded(true);
        }
      } else {
        console.error('Failed to fetch invitations:', response.status);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setFetchingInvitations(false);
    }
  };

  // Filter invitations based on search
  const filteredInvitations = useMemo(() => {
    return invitations.filter(invitation => {
      const matchesSearch = invitation.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           invitation.invitedBy.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [invitations, searchQuery]);

  // Cancelling the only row on the last page, or a stale link, leaves the URL
  // past the end; the helper shows the last real page and corrects the URL.
  const { rows: paginatedInvitations, currentPage } = useClientPage(
    { page, limit: pageSize, clampPage },
    filteredInvitations,
    invitationsLoaded,
  );

  const handleClearFilters = useCallback(() => {
    handleSearchChange('');
  }, [handleSearchChange]);

  const getActiveFilters = useMemo((): ActiveFilter[] => {
    const activeFilters: ActiveFilter[] = [];

    if (searchQuery) {
      activeFilters.push({
        key: 'search',
        label: `"${searchQuery}"`,
        value: searchQuery,
        onRemove: () => handleSearchChange('')
      });
    }

    return activeFilters;
  }, [searchQuery, handleSearchChange]);

  const globalFilterFn = useCallback((invitation: Invitation, searchQuery: string) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      invitation.email.toLowerCase().includes(searchLower) ||
      invitation.invitedBy.toLowerCase().includes(searchLower) ||
      invitation.status.toLowerCase().includes(searchLower) ||
      invitation.role.toLowerCase().includes(searchLower)
    );
  }, []);

  const getStatusBadge = useCallback((status: string) => {
    const configs = {
      pending: {
        label: t('common.pending'),
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
        icon: ClockIcon
      },
      accepted: {
        // Was t('people.invitationResent') with a defaultValue of 'Accepted'.
        // That key exists, so the default never applied and an accepted
        // invitation was labelled "Invitation resent".
        label: t('invitations.statusAccepted'),
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        icon: CheckCircleIcon
      },
      expired: {
        label: t('invitations.statusExpired'),
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
        icon: ExclamationTriangleIcon
      }
    };

    const config = configs[status as keyof typeof configs] || configs.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  }, [t]);

  const getRoleBadge = useCallback((role: string) => {
    const roleConfig = {
      admin: { label: t('common.admin') },
      author: { label: t('roles.author') },
      contributor: { label: t('roles.contributor') },
      viewer: { label: t('common.viewer') }
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.viewer;

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
        {config.label}
      </span>
    );
  }, [t]);

  const columns: DataTableColumn<Invitation>[] = useMemo(() => [
    {
      id: 'invitee',
      header: t('invitations.invitee'),
      accessorKey: 'email',
      enableSorting: false,
      size: 280,
      cell: (invitation: Invitation) => (
        <div>
          <div className="text-sm font-medium text-foreground">
            {invitation.email}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('invitations.invitedByOn', { name: invitation.invitedBy, date: formatDateInTz(invitation.createdAt, timezone) })}
          </div>
        </div>
      ),
    },
    {
      id: 'role',
      header: t('common.role'),
      accessorKey: 'role',
      enableSorting: false,
      size: 90,
      cell: (invitation: Invitation) => getRoleBadge(invitation.role),
    },
    {
      id: 'apps',
      header: t('invitations.apps'),
      accessorKey: 'apps',
      enableSorting: false,
      size: 90,
      cell: (invitation: Invitation) => {
        // Filter out Dashboard app as it's accessible to everyone in the tenant
        const relevantApps = invitation.apps.filter(app => {
          // Check if the app name or id indicates Dashboard
          return app.name?.toLowerCase() !== 'dashboard' && app.id?.toLowerCase() !== 'dashboard';
        });

        return (
          <AppsStack
            apps={relevantApps}
            maxVisible={3}
            size="xs"
            showTooltip={true}
          />
        );
      },
    },
    {
      id: 'status',
      header: t('common.status'),
      accessorKey: 'status',
      enableSorting: false,
      size: 100,
      cell: (invitation: Invitation) => getStatusBadge(invitation.status),
    },
    {
      id: 'expires',
      header: t('invitations.expires'),
      accessorKey: 'expiresAt',
      enableSorting: false,
      size: 100,
      cell: (invitation: Invitation) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDateInTz(invitation.expiresAt, timezone)}
        </span>
      ),
    },
  ], [getRoleBadge, getStatusBadge, t, timezone]);

  /**
   * Prefer the server's own words when it gave us any.
   *
   * The API client throws an Error carrying the envelope's `error.code` and
   * `error.message`, so a reason like "that invitation has already been
   * accepted" can be shown as-is. A transport failure has no code or status and
   * falls back to the generic copy rather than surfacing "Failed to fetch".
   */
  const serverReason = useCallback((err: any, fallback: string) => {
    return (err?.status || err?.code) && err?.message ? err.message : fallback;
  }, []);

  /**
   * Re-send an invitation.
   *
   * This used to be a one-second setTimeout that pushed a new expiry date into
   * local state — the row moved, the operator believed a mail had gone out, and
   * nothing had been sent. The expiry now comes back from the server, so the
   * list is re-read rather than guessed at.
   */
  const handleResendInvite = useCallback(async (invitationId: string) => {
    setActionInFlight(invitationId);
    try {
      await dashboardApiClient.resendTeamInvitation(invitationId);
      toastSuccess(t('people.invitationResent'));
      await fetchInvitations();
    } catch (err: any) {
      console.error('Failed to resend invitation:', err);
      toastError(serverReason(err, t('invitations.failedToResend')));
    } finally {
      setActionInFlight(null);
    }
  }, [t, toastSuccess, toastError, serverReason]);

  /**
   * Cancel an invitation, once confirmed.
   *
   * The stub this replaces flipped the row to a 'cancelled' badge in local
   * state and called nothing. The badge survived until the next fetch, the
   * token stayed live the whole time, and the invitee could still accept.
   */
  const handleCancelInvite = useCallback(async () => {
    if (!invitationToCancel) return;
    const invitationId = invitationToCancel.id;
    setActionInFlight(invitationId);
    try {
      await dashboardApiClient.cancelTeamInvitation(invitationId);
      setInvitationToCancel(null);
      toastSuccess(t('invitations.invitationCancelled'));
      await fetchInvitations();
    } catch (err: any) {
      console.error('Failed to cancel invitation:', err);
      toastError(serverReason(err, t('invitations.failedToCancel')));
    } finally {
      setActionInFlight(null);
    }
  }, [invitationToCancel, t, toastSuccess, toastError, serverReason]);

  const actions: DataTableAction<Invitation>[] = useMemo(() => [
    {
      id: 'resend',
      label: t('invitations.resendInvitation'),
      icon: PaperAirplaneIcon,
      onClick: (invitation: Invitation) => handleResendInvite(invitation.id),
      disabled: (invitation: Invitation) =>
        actionInFlight === invitation.id ||
        (invitation.status !== 'pending' && invitation.status !== 'expired'),
    },
    {
      id: 'cancel',
      label: t('invitations.cancelInvitation'),
      icon: XMarkIcon,
      onClick: (invitation: Invitation) => setInvitationToCancel(invitation),
      variant: 'destructive',
      disabled: (invitation: Invitation) =>
        actionInFlight === invitation.id || invitation.status !== 'pending',
    },
  ], [handleResendInvite, actionInFlight, t]);

  return (
    <div className="space-y-4">
      {!hideHeader && <PageHeader title={t('invitations.teamInvitations')} />}

      {/* Search and Filters */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="w-full max-w-sm">
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('invitations.searchInvitations')}
              onClear={() => handleSearchChange('')}
              showClearButton={true}
              size="md"
            />
          </div>

          <div className="flex justify-end sm:justify-start">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredInvitations.length}
              itemsPerPage={pageSize}
              onPageChange={onPageChange}
            />
          </div>
        </div>

        {getActiveFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('people.activeFilters')}</span>
            {getActiveFilters.map((filter) => (
              <div key={filter.key} className="flex items-center gap-1 px-2 py-1 bg-primary/15 dark:bg-primary/25 text-primary dark:text-[hsl(var(--primary-hover))] text-xs rounded-full">
                <span>{filter.label}</span>
                <button
                  onClick={filter.onRemove}
                  className="ml-1 hover:bg-primary/25 dark:hover:bg-primary/35 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-2 w-2" />
                </button>
              </div>
            ))}
            <button
              onClick={handleClearFilters}
              className="ml-auto text-xs h-6 px-2 text-primary hover:text-[hsl(var(--primary-hover))] hover:bg-primary/10 dark:hover:bg-primary/15 rounded"
            >
              {t('people.clearAll')}
            </button>
          </div>
        )}
      </div>

      {/* Invitations Table */}
      <div className="overflow-x-auto">
        <DataTable
          data={paginatedInvitations}
          columns={columns}
          actions={actions}
          searchQuery={searchQuery}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          isLoading={fetchingInvitations}
          error={null}
          onRetry={fetchInvitations}
          globalFilterFn={globalFilterFn}
          emptyMessage={t('invitations.noInvitationsFound')}
          enablePagination={false}
        />
      </div>

      <ConfirmationModal
        isOpen={invitationToCancel !== null}
        onClose={() => setInvitationToCancel(null)}
        title={t('invitations.cancelInvitation')}
        description={t('invitations.cancelConfirm')}
        variant="danger"
        confirmText={t('invitations.cancelInvitation')}
        // Not common.cancel: the destructive verb here is itself "cancel", so a
        // dialog offering "Cancel Invitation" beside "Cancel" asks the operator
        // to work out which Cancel does nothing.
        cancelText={t('invitations.keepInvitation')}
        onConfirm={handleCancelInvite}
        isLoading={invitationToCancel !== null && actionInFlight === invitationToCancel.id}
      />
    </div>
  );
}
